import { BlockType } from './../constants';
import { IBlock, IBlockData } from '@core/typings';
import { get, isString } from 'lodash';
import { ancestorOf } from './ancestorOf';
import { BlockManager } from './BlockManager';
import {
  CUSTOM_LISTING_BLOCK_TYPE,
  isDynamicListingColumn,
  isValidParentType,
} from './listingScope';
import { getListing, isListingLoading, isListingUnavailable } from './listingCache';

// Re-exported so consumers keep importing these from one place.
export {
  CUSTOM_LISTING_BLOCK_TYPE,
  SINGLE_LISTING_BLOCK_TYPE,
  MULTI_LISTING_BLOCK_TYPE,
  LISTING_PICKER_CATEGORY_BY_TYPE,
  LISTING_PICKER_CATEGORIES,
  isListingPickerType,
  isDeniedListingPlacement,
  isDynamicListingColumn,
  isListingBlockType,
  isListingScopeBlock,
  isListingSectionPayload,
  isValidParentType,
} from './listingScope';

export function getPageIdx() {
  return 'content';
}

export function getChildIdx(idx: string, index: number) {
  return `${idx}.children.[${index}]`;
}

export function getNodeIdxClassName(idx: string) {
  return `node-idx-${idx}`;
}

export function getNodeTypeClassName(type: string) {
  return `node-type-${type}`;
}

export function getNodeIdxFromClassName(classList: DOMTokenList) {
  return Array.from(classList)
    .find((item) => item.includes('node-idx-'))
    ?.replace('node-idx-', '');
}

export function getNodeTypeFromClassName(
  classList: DOMTokenList | string
): BlockType | null {
  return Array.from(isString(classList) ? classList.split(' ') : classList)
    .find((item) => item.includes('node-type-'))
    ?.replace('node-type-', '') as BlockType;
}

export const getIndexByIdx = (idx: string) => {
  return Number(/\.\[(\d+)\]$/.exec(idx)?.[1]) || 0;
};

export const getParentIdx = (idx: string) => {
  if (idx === getPageIdx()) return undefined;
  return /(.*)\.children\.\[\d+\]$/.exec(idx)?.[1];
};

export const getValueByIdx = <T extends IBlockData>(
  values: { content: IBlockData },
  idx: string
): T | null => {
  return get(values, idx);
};

export const getParentByIdx = <T extends IBlockData = IBlockData>(
  values: { content: IBlockData },
  idx: string
): T | null => {
  return get(values, getParentIdx(idx) || '');
};

export const getSiblingIdx = (sourceIndex: string, num: number) => {
  return sourceIndex.replace(/\[(\d+)\]$/, (_, index) => {
    if (Number(index) + num < 0) return '[0]';
    return `[${Number(index) + num}]`;
  });
};

export const getParentByType = <T extends IBlockData>(
  context: { content: IBlockData },
  idx: string,
  type: BlockType
): T | null => {
  if (!idx) return null;
  let parentIdx = getParentIdx(idx);
  while (parentIdx) {
    const parent = get(context, parentIdx) as T;
    if (parent && parent.type === type) return parent;
    parentIdx = getParentIdx(idx);
  }

  return null;
};

export const getSameParent = (
  values: { content: IBlockData },
  idx: string,
  dragType: string
): {
  parent: IBlockData;
  parentIdx: string;
} | null => {
  let parentIdx: string | undefined | null = idx;
  const block = BlockManager.getBlockByType(dragType);
  if (!block) return null;

  while (parentIdx) {
    const parent = get(values, parentIdx) as IBlockData;

    if (ancestorOf(block.type, parent.type) > 0) {
      return {
        parent,
        parentIdx,
      };
    }
    parentIdx = getParentIdx(parentIdx);
  }
  return null;
};

// 找到插入的位置，例如 一个 2 column section，第二个 column/image，image占满column, 拖拽 一个column到 image的边缘，我们认为他是要插入一个column，获取这个插入的位置，我们这里是 第二个，所以 是 1
export const getParenRelativeByType = <T extends IBlockData>(
  context: { content: IBlockData },
  idx: string,
  type: BlockType
): { parentIdx: string; insertIndex: number; parent: IBlockData } | null => {
  let prevIdx = '';
  let parentIdx: string | undefined = idx;
  while (parentIdx) {
    const parent = get(context, parentIdx) as T;
    if (parent && parent.type === type) {
      return {
        insertIndex: prevIdx
          ? getIndexByIdx(prevIdx)
          : parent.children.length - 1,
        parentIdx,
        parent,
      };
    } else {
      prevIdx = parentIdx;
      parentIdx = getParentIdx(parentIdx);
    }
  }
  return null;
};

function walkUp(
  context: IBlockData | null | undefined,
  idx: string | null | undefined,
  match: (block: IBlockData) => boolean
): string | null {
  if (!context || !idx) return null;
  const values = { content: context };
  let parentIdx = getParentIdx(idx);
  while (parentIdx) {
    const parent = get(values, parentIdx) as IBlockData | undefined;
    if (parent && match(parent)) return parentIdx;
    parentIdx = getParentIdx(parentIdx);
  }
  return null;
}

/**
 * The block that owns the listing for this node, whether or not one is picked
 * yet. Used by the merge-tag gate and the child "Select Listing" retarget.
 *
 * A Listing Wrapper ALWAYS wins. Its listing cascades to every element and
 * column inside it, with no exceptions — a column cannot contradict the region
 * it sits in. Per-column listings come from the sidebar's "Listing" section
 * variant and are meant for sections OUTSIDE a Listing Wrapper; if one ends up
 * inside anyway, the wrapper still decides.
 *
 * NOTE: getParentByType above is buggy (never ascends) — these ascend correctly
 * via getParentIdx(parentIdx).
 */
export function getAncestorCustomListingIdx(
  context: IBlockData | null | undefined,
  idx: string | null | undefined
): string | null {
  return (
    walkUp(context, idx, (block) => block.type === CUSTOM_LISTING_BLOCK_TYPE) ??
    walkUp(context, idx, isDynamicListingColumn) ??
    // Last resort: any ancestor that actually holds a listing. A plain column or
    // section can be given one without carrying the sidebar's isDynamicListing
    // flag, and without this its children inherited nothing — the child's panel
    // offered "Select Listing" for a listing its parent had already picked.
    walkUp(context, idx, (block) =>
      Boolean(
        (block as any)?.data?.value?.selectedListingId ||
          (block as any)?.data?.value?.selectedListing
      )
    )
  );
}

export function getAncestorCustomListing(
  context: IBlockData | null | undefined,
  idx: string | null | undefined
): IBlockData | null {
  const ancestorIdx = getAncestorCustomListingIdx(context, idx);
  if (!ancestorIdx || !context) return null;
  return (get({ content: context }, ancestorIdx) as IBlockData) || null;
}

/**
 * The listing id a block resolves against — the owning scope's if there is one,
 * otherwise the block's own. Same precedence as `getAncestorPinnedListing() ||
 * resolveBoundListing()`, but it answers before the data arrives, which is the
 * whole point: the resolved-listing helpers return null while a lookup is in
 * flight, so nothing downstream could tell "no listing" apart from "not here
 * yet".
 */
export function getBoundListingId(
  context: IBlockData | null | undefined,
  idx: string | null | undefined,
  block: IBlockData | null | undefined
): string {
  const scopeValue = (getAncestorCustomListing(context, idx) as any)?.data?.value;
  const ownValue = (block as any)?.data?.value;

  return (
    scopeValue?.selectedListingId ||
    scopeValue?.selectedListing?.id ||
    ownValue?.selectedListingId ||
    ownValue?.selectedListing?.id ||
    ''
  );
}

/**
 * Whether that binding is still being fetched. Callers use it to hold off on
 * "nothing here" messaging — an unresolved listing-bound field is a warning
 * once the lookup has finished and come back empty, and a spinner until then.
 */
export function isBoundListingResolving(
  context: IBlockData | null | undefined,
  idx: string | null | undefined,
  block: IBlockData | null | undefined
): boolean {
  return isListingLoading(getBoundListingId(context, idx, block));
}

/**
 * The listing a block hydrates from — its owner's, once that owner has one
 * picked. Nothing is inherited past the owner: a listing column with no listing
 * yet renders unresolved rather than quietly borrowing from further up.
 */
export function getAncestorPinnedListing(
  context: IBlockData | null | undefined,
  idx: string | null | undefined
): Record<string, any> | null {
  const pinnedValue = (getAncestorCustomListing(context, idx) as any)?.data
    ?.value;

  return resolveBoundListing(pinnedValue);
}

/**
 * The listing a block is bound to, resolved live.
 *
 * Bindings persist as an id; the values come from the render cache, which
 * fetches on demand. `selectedListing` is only a pre-migration leftover — read
 * as a first paint until the lookup lands, never as the source of truth.
 *
 * Returns null for a listing the resolver reported unavailable, so dynamic
 * fields land on their configured `{{#if}}…{{else}}` fallback instead of
 * showing values from a listing that has since sold or been deleted.
 */
export function resolveBoundListing(
  value:
    | { selectedListingId?: string; selectedListing?: Record<string, any> | null }
    | null
    | undefined
): Record<string, any> | null {
  if (!value) return null;

  const boundId = value.selectedListingId || value.selectedListing?.id;
  if (!boundId) return value.selectedListing || null;

  if (isListingUnavailable(boundId)) return null;

  return getListing(boundId) || value.selectedListing || null;
}

/**
 * The Listing Wrapper a block sits in, ignoring any column scope between them.
 * A column uses this to know its own listing is moot: inside a wrapper, the
 * wrapper's listing cascades and the column's picker is not shown.
 */
export function getAncestorListingWrapper(
  context: IBlockData | null | undefined,
  idx: string | null | undefined
): IBlockData | null {
  const wrapperIdx = walkUp(
    context,
    idx,
    (block) => block.type === CUSTOM_LISTING_BLOCK_TYPE
  );
  if (!wrapperIdx || !context) return null;
  return (get({ content: context }, wrapperIdx) as IBlockData) || null;
}

export function getValidChildBlocks(type: BlockType): IBlock[] {
  return BlockManager.getBlocks().filter((item) => isValidParentType(item, type));
}
